import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { PLANBASE_CONFIG } from '../config';

// Slides shown in the onboarding carousel
const slides = [
  {
    id: 1,
    headline: 'Build your story',
    body: `Showcase your architectural work with a portfolio that's as bold as your designs.`,
    // Subtle architectural grid pattern via SVG background
    accent: 'from-orange-500/20 to-orange-600/5',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" className="w-20 h-20" aria-hidden>
        <rect x="8" y="40" width="16" height="32" rx="2" fill="currentColor" className="text-primary" />
        <rect x="32" y="24" width="16" height="48" rx="2" fill="currentColor" className="text-orange-400" />
        <rect x="56" y="12" width="16" height="60" rx="2" fill="currentColor" className="text-orange-300" />
        <line x1="4" y1="72" x2="76" y2="72" stroke="currentColor" strokeWidth="2" className="text-border" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 2,
    headline: 'Discover great work',
    body: 'Explore projects from architects and designers around the world — filtered by style, type, and category.',
    accent: 'from-orange-500/20 to-orange-600/5',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" className="w-20 h-20" aria-hidden>
        <circle cx="34" cy="34" r="22" stroke="currentColor" strokeWidth="3" className="text-orange-400" />
        <line x1="50" y1="50" x2="68" y2="68" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="text-primary" />
        <circle cx="34" cy="34" r="12" fill="currentColor" className="text-primary/20" />
      </svg>
    ),
  },
  {
    id: 3,
    headline: 'AI-powered insights',
    body: 'Orbit AI helps you write project descriptions, get design feedback, and understand your portfolio performance.',
    accent: 'from-orange-500/20 to-orange-600/5',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" className="w-20 h-20" aria-hidden>
        <circle cx="40" cy="40" r="18" fill="currentColor" className="text-primary/20" />
        <circle cx="40" cy="40" r="8" fill="currentColor" className="text-primary" />
        <circle cx="40" cy="14" r="5" fill="currentColor" className="text-orange-400" />
        <circle cx="40" cy="66" r="5" fill="currentColor" className="text-orange-400" />
        <circle cx="14" cy="40" r="5" fill="currentColor" className="text-orange-400" />
        <circle cx="66" cy="40" r="5" fill="currentColor" className="text-orange-400" />
      </svg>
    ),
  },
];

export default function IntroPage({ onDone }: { onDone?: () => void }) {
  const navigate = useNavigate();

  const finish = (path: string) => {
    onDone?.();
    navigate(path);
  };
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const autoplayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = (index: number) => {
    if (animating || index === current) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 200);
  };

  const next = () => {
    if (current < slides.length - 1) {
      goTo(current + 1);
    }
  };

  // Auto-advance slides
  useEffect(() => {
    autoplayRef.current = setTimeout(() => {
      if (current < slides.length - 1) {
        goTo(current + 1);
      }
    }, 3500);
    return () => {
      if (autoplayRef.current) clearTimeout(autoplayRef.current);
    };
  }, [current]);

  const isLast = current === slides.length - 1;
  const slide = slides[current];

  return (
    <div className="min-h-screen bg-background flex flex-col">

      {/* Skip — top right */}
      <div className="flex justify-end px-6 pt-6 pb-2">
        <button
          onClick={() => finish('/login')}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip
        </button>
      </div>

      {/* Main content — grows to fill available space */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 pb-4">

        {/* Illustration card */}
        <div
          className={`
            w-full max-w-xs rounded-3xl bg-gradient-to-b ${slide.accent}
            border border-border
            flex flex-col items-center justify-center
            py-12 px-6 mb-10
            transition-opacity duration-200
            ${animating ? 'opacity-0' : 'opacity-100'}
          `}
        >
          {/* Logo mark */}
          <div className="mb-8">
            <img
              src={PLANBASE_CONFIG.project.logo_url}
              alt="Planbase"
              className="h-10 w-auto"
              onError={(e) => {
                // Fallback if logo fails to load
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>

          {/* Slide icon */}
          <div className="mb-6">
            {slide.icon}
          </div>

          {/* Slide dot indicators — inside the card */}
          <div className="flex gap-2 mt-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  i === current
                    ? 'w-6 h-2 bg-primary'
                    : 'w-2 h-2 bg-border hover:bg-muted-foreground'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Text content */}
        <div
          className={`text-center transition-opacity duration-200 ${animating ? 'opacity-0' : 'opacity-100'}`}
        >
          <h1 className="text-2xl font-semibold text-foreground mb-3 leading-tight">
            {slide.headline}
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-[280px] mx-auto">
            {slide.body}
          </p>
        </div>
      </div>

      {/* Bottom CTA area */}
      <div className="px-6 pb-10 pt-4 flex flex-col gap-3 w-full max-w-sm mx-auto">
        {isLast ? (
          <>
            <Button
              mobileFullHeight
              className="w-full"
              onClick={() => finish('/signup')}
            >
              Get started — it's free
            </Button>
            <Button
              variant="outline"
              mobileFullHeight
              className="w-full"
              onClick={() => finish('/login')}
            >
              I already have an account
            </Button>
          </>
        ) : (
          <>
            <Button
              mobileFullHeight
              className="w-full"
              onClick={next}
            >
              Next
            </Button>
            <button
              onClick={() => finish('/login')}
              className="text-sm text-muted-foreground hover:text-foreground text-center py-2 transition-colors"
            >
              I already have an account
            </button>
          </>
        )}
      </div>
    </div>
  );
}
